import React, { useState } from "react"
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack, TextField, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import { asDialogData } from "../../../core/utils/utils"
import {
    useGetAllMascotsQuery,
    useGetMascotsQuery,
    useGetAllExperienceCategoriesQuery,
    useGetActiveSeasonQuery,
    useCreateMascotMutation,
    useUpdateMascotMutation,
    useDeleteMascotMutation,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"

// Admin CRUD for mascot info-cards. Mascots are static blocks shown on the
// My Experience page (image + title + description). Editable text per mascot.
export default function MascotsAdmin() {
    // /experience/mascots/all is admin-only — use that to also get inactive ones,
    // but fall back to /mascots for safety.
    const { data: allMascots, isLoading: loadingAll } = useGetAllMascotsQuery()
    const { data: activeMascots = [] } = useGetMascotsQuery()
    const { data: categories = [] } = useGetAllExperienceCategoriesQuery()
    const { data: activeSeason } = useGetActiveSeasonQuery()
    const mascots = allMascots && allMascots.length ? allMascots : activeMascots

    const [createMascot, createResp] = useCreateMascotMutation()
    const [updateMascot, updateResp] = useUpdateMascotMutation()
    const [deleteMascot] = useDeleteMascotMutation()

    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState({ slug: "", title: "", description: "", imageUrl: "", categoryId: "", sortOrder: 0, active: true, season: "" })
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    function openNew() {
        setEditing({})
        setForm({ slug: "", title: "", description: "", imageUrl: "", categoryId: "", sortOrder: (mascots.length || 0) + 1, active: true, season: activeSeason?.number ?? "" })
    }
    function openEdit(row) {
        setEditing(row)
        setForm({
            slug: row.slug, title: row.title, description: row.description || "",
            imageUrl: row.imageUrl || "",
            categoryId: row.categoryId || "",
            sortOrder: row.sortOrder || 0, active: !!row.active,
            season: row.season ?? "",
        })
    }
    function close() { setEditing(null) }

    async function save() {
        const body = {
            slug: form.slug.trim(),
            title: form.title.trim(),
            description: form.description,
            imageUrl: form.imageUrl,
            categoryId: form.categoryId || "",
            sortOrder: parseInt(form.sortOrder, 10) || 0,
            active: form.active,
        }
        // Сезон задаётся только при создании; пустое поле → активный сезон.
        if (!editing.id && form.season !== "") {
            body.season = parseInt(form.season, 10)
        }
        const resp = editing.id
            ? await updateMascot({ id: editing.id, ...body })
            : await createMascot(body)
        setActionResp(asDialogData(resp))
        setRespOpen(true)
        if (!resp.error) close()
    }
    async function remove(row) {
        if (!window.confirm(`Delete mascot «${row.title}»?`)) return
        const resp = await deleteMascot(row.id)
        setActionResp(asDialogData(resp))
        setRespOpen(true)
    }

    const columns = [
        { field: "season", headerName: "Season", width: 90, renderCell: (p) => p.value ?? "—" },
        { field: "sortOrder", headerName: "#", width: 60 },
        { field: "imageUrl", headerName: "Image", width: 80, renderCell: (p) =>
            p.value
                ? <img src={p.value} alt="" style={{ width: 40, height: 40, objectFit: "contain" }} />
                : <span style={{ color: "#aaa" }}>—</span>
        },
        { field: "slug", headerName: "Slug", width: 140 },
        { field: "title", headerName: "Title", width: 160 },
        { field: "categoryId", headerName: "Category", width: 130, renderCell: (p) => {
            const c = categories.find(x => x.id === p.value)
            return c ? c.name : "—"
        }},
        { field: "description", headerName: "Description", flex: 1 },
        { field: "active", headerName: "Active", width: 90, renderCell: (p) => p.value ? "yes" : "no" },
        { field: "actions", headerName: "", width: 100, renderCell: (p) =>
            <Stack direction="row">
                <IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(p.row)}><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
        },
    ]

    if (loadingAll) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Mascots</Typography>
                <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={openNew}>
                    New mascot
                </Button>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
                Mascots are informational cards on the My Experience page (not earned achievements).
                Image path is relative to the frontend (e.g. <code>/mascotes/Brave-Bard_1_color_500x500.png</code>).
            </Alert>
            <Paper sx={{ height: 560 }}>
                <DataGrid
                    rows={mascots}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={!!editing} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.id ? "Edit mascot" : "New mascot"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Slug"
                            helperText="Lowercase identifier, e.g. brave-bard"
                            value={form.slug}
                            onChange={e => setForm({ ...form, slug: e.target.value })}
                            required
                        />
                        <TextField
                            label="Title"
                            value={form.title}
                            onChange={e => setForm({ ...form, title: e.target.value })}
                            required
                        />
                        <TextField
                            label="Description"
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            multiline
                            minRows={3}
                        />
                        <TextField
                            label="Image path"
                            helperText="Relative path under the frontend public folder, e.g. /mascotes/Brave-Bard_1_color_500x500.png"
                            value={form.imageUrl}
                            onChange={e => setForm({ ...form, imageUrl: e.target.value })}
                        />
                        <TextField
                            select
                            label="Linked XP category"
                            helperText="Mascot card on My Experience will show this category's progress"
                            value={form.categoryId}
                            onChange={e => setForm({ ...form, categoryId: e.target.value })}
                        >
                            <MenuItem value="">— none —</MenuItem>
                            {categories.filter(c => c.active !== false).map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}{c.season ? ` (сезон ${c.season})` : ""}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Sort order"
                            type="number"
                            value={form.sortOrder}
                            onChange={e => setForm({ ...form, sortOrder: e.target.value })}
                        />
                        <TextField
                            label="Season"
                            type="number"
                            helperText={editing?.id ? "Сезон записи менять нельзя" : "Пусто — активный сезон; будущий номер — подготовка белок заранее"}
                            value={form.season}
                            onChange={e => setForm({ ...form, season: e.target.value })}
                            disabled={!!editing?.id}
                        />
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <input
                                type="checkbox"
                                checked={!!form.active}
                                onChange={e => setForm({ ...form, active: e.target.checked })}
                                id="mascot-active"
                            />
                            <label htmlFor="mascot-active">Active</label>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={save} disabled={createResp.isLoading || updateResp.isLoading}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="Mascot"
                information="Saved"
            />
        </Box>
    )
}
